
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, FileUpload, emiratesOptions } from "./BaseForm";
import { WorkersCompensationFormData, FormProps } from "@/types/forms";

type WorkersCompensationFormProps = FormProps<WorkersCompensationFormData>;

const WorkersCompensationForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: WorkersCompensationFormProps) => {
  const [formData, setFormData] = useState<WorkersCompensationFormData>({
    numberOfEmployees: initialData.numberOfEmployees || "",
    employeeRoles: initialData.employeeRoles || "",
    annualWages: initialData.annualWages || "",
    businessEmirate: initialData.businessEmirate || "",
    tradeLicense: initialData.tradeLicense || null,
  });
  const [noTradeLicense, setNoTradeLicense] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = "Number of employees is required";
    if (!formData.employeeRoles) newErrors.employeeRoles = "Employee roles description is required";
    if (!formData.annualWages) newErrors.annualWages = "Annual wages amount is required";
    if (!formData.businessEmirate) newErrors.businessEmirate = "Business emirate is required";

    // Only require trade license if checkbox is not checked
    if (!formData.tradeLicense && !noTradeLicense) {
      newErrors.tradeLicense = "Please upload your trade license or check the box below";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof WorkersCompensationFormData>(field: K, value: WorkersCompensationFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setNoTradeLicense(checked);
    if (checked) {
      // Clear trade license file when checkbox is checked
      setFormData(prev => ({ ...prev, tradeLicense: null }));
      // Clear any existing errors
      setErrors(prev => ({ ...prev, tradeLicense: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Number of employees" required>
        <Input
          type="number"
          placeholder="Enter number of employees"
          value={formData.numberOfEmployees}
          onChange={(e) => updateField('numberOfEmployees', e.target.value)}
        />
        {errors.numberOfEmployees && <p className="text-red-500 text-sm">{errors.numberOfEmployees}</p>}
      </FormField>

      <FormField label="Type of employee roles" required>
        <Textarea
          placeholder="Brief description of employee roles and responsibilities"
          value={formData.employeeRoles}
          onChange={(e) => updateField('employeeRoles', e.target.value)}
        />
        {errors.employeeRoles && <p className="text-red-500 text-sm">{errors.employeeRoles}</p>}
      </FormField>

      <FormField label="Total annual wages amount (AED)" required>
        <Input
          type="number"
          placeholder="Enter total annual wages in AED"
          value={formData.annualWages}
          onChange={(e) => updateField('annualWages', e.target.value)}
        />
        {errors.annualWages && <p className="text-red-500 text-sm">{errors.annualWages}</p>}
      </FormField>

      <FormField label="Emirate of business license" required>
        <Select value={formData.businessEmirate} onValueChange={(value) => updateField('businessEmirate', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select emirate" />
          </SelectTrigger>
          <SelectContent>
            {emiratesOptions.map((emirate) => (
              <SelectItem key={emirate} value={emirate}>
                {emirate}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.businessEmirate && <p className="text-red-500 text-sm">{errors.businessEmirate}</p>}
      </FormField>

      <FileUpload
        label="Upload Trade License"
        accept=".pdf,.jpg,.jpeg,.png"
        required={false}
        value={formData.tradeLicense}
        onChange={(file) => updateField('tradeLicense', file)}
        disabled={noTradeLicense}
      />

      <div className="flex items-center space-x-2">
        <Checkbox
          id="no-trade-license-workers"
          checked={noTradeLicense}
          onCheckedChange={handleCheckboxChange}
        />
        <label
          htmlFor="no-trade-license-workers"
          className="text-sm text-gray-700 cursor-pointer"
        >
          I don't have my trade license with me
        </label>
      </div>

      {errors.tradeLicense && <p className="text-red-500 text-sm">{errors.tradeLicense}</p>}

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default WorkersCompensationForm;
